import { useEffect, useState } from 'react';
import { ACHIEVEMENT_DEFINITIONS, ACHIEVEMENT_TIERS, SHOP_ITEM_MAP, type AchievementTier } from '@flip7/shared';
import { useAuth } from '../contexts/AuthContext';
import { Spinner } from './Spinner';

const API_BASE = import.meta.env.VITE_API_URL || '';

const TIER_COLORS: Record<AchievementTier, string> = {
  bronze: '#CD7F32',
  silver: '#A0A0A0',
  gold: '#DAA520',
};

interface AchievementEntry {
  achievementKey: string;
  tier: string;
  progress: number;
}

export function AchievementsPanel() {
  const { accessToken, user, updateTitle } = useAuth();
  const [achievements, setAchievements] = useState<AchievementEntry[]>([]);
  const [unlockedTitles, setUnlockedTitles] = useState<string[]>([]);
  const [inventory, setInventory] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!accessToken) return;
    fetch(`${API_BASE}/api/users/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.achievements) setAchievements(data.achievements);
        if (data?.unlockedTitles) setUnlockedTitles(data.unlockedTitles);
        if (data?.inventory) setInventory(data.inventory);
      })
      .finally(() => setLoading(false));
  }, [accessToken]);

  if (!accessToken) {
    return (
      <div className="social-empty">
        <p className="social-empty__text">Sign in to view achievements.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="social-empty">
        <Spinner small />
      </div>
    );
  }

  const achievementMap = new Map(achievements.map(a => [a.achievementKey, a]));

  // Title picker — same logic as ProfileScreen
  const purchasedTitles = inventory
    .filter(key => SHOP_ITEM_MAP[key]?.type === 'title')
    .map(key => SHOP_ITEM_MAP[key].titleText!)
    .filter(Boolean);
  const allTitles = [...new Set([...unlockedTitles, ...purchasedTitles])];

  return (
    <div className="achievements-panel">
      {/* Title picker */}
      {allTitles.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <p className="drawer-title" style={{ marginBottom: '6px' }}>Active Title</p>
          <div className="picker-grid">
            <button
              onClick={() => updateTitle(null)}
              className={`picker-item picker-item--pill${!user?.activeTitle ? ' picker-item--active-maroon' : ''}`}
            >
              None
            </button>
            {allTitles.map(title => (
              <button
                key={title}
                onClick={() => updateTitle(title)}
                className={`picker-item picker-item--pill${user?.activeTitle === title ? ' picker-item--active' : ''}`}
              >
                {title}
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="drawer-title" style={{ marginBottom: '4px' }}>Achievements</p>
      {ACHIEVEMENT_DEFINITIONS.map(def => {
        const ua = achievementMap.get(def.key);
        const currentTier = ua?.tier as AchievementTier | undefined;
        const progress = ua?.progress ?? 0;
        const tierIndex = currentTier ? ACHIEVEMENT_TIERS.indexOf(currentTier) : -1;
        const nextTier = tierIndex < 2 ? ACHIEVEMENT_TIERS[tierIndex + 1] : null;
        const nextThreshold = nextTier ? def.tiers[nextTier].threshold : null;

        return (
          <div
            key={def.key}
            className={`ornate-border achievement-card${currentTier ? '' : ' achievement-card--locked'}`}
          >
            <div className="achievement-card__row">
              <span className="achievement-card__icon">{def.icon}</span>
              <div className="achievement-card__body">
                <div className="achievement-card__header">
                  <span className="achievement-card__name">{def.name}</span>
                  {currentTier && (
                    <span className="achievement-card__tier" style={{ color: TIER_COLORS[currentTier] }}>
                      {currentTier}
                    </span>
                  )}
                </div>
                <p className="achievement-card__desc">{def.description}</p>
                {nextThreshold && (
                  <div className="achievement-card__progress">
                    <div className="achievement-card__bar">
                      <div
                        className="achievement-card__bar-fill"
                        style={{
                          width: `${Math.min(100, (progress / nextThreshold) * 100)}%`,
                          background: nextTier ? TIER_COLORS[nextTier] : 'var(--gold)',
                        }}
                      />
                    </div>
                    <span className="achievement-card__count">
                      {progress} / {nextThreshold}
                    </span>
                  </div>
                )}
                {currentTier === 'gold' && (
                  <span className="achievement-card__max" style={{ color: TIER_COLORS.gold }}>
                    MAX
                  </span>
                )}
              </div>
              <div className="achievement-card__dots">
                {ACHIEVEMENT_TIERS.map(t => {
                  const earned = currentTier && ACHIEVEMENT_TIERS.indexOf(t) <= tierIndex;
                  return (
                    <div
                      key={t}
                      className={`achievement-card__dot${earned ? '' : ' achievement-card__dot--empty'}`}
                      style={earned ? { background: TIER_COLORS[t] } : undefined}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
